import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { getEmpresaAtual } from './api';
import {
  coresHex, hexParaRgb, escurecer, clarear,
} from '../styles/styleUtils';

// Converte um hex de coresHex em rgb() 0-1 do pdf-lib (que não entende var()/hex).
const rgbHex = (hex) => {
  const { r, g, b } = hexParaRgb(hex);
  return rgb(r / 255, g / 255, b / 255);
};

// ── Gráfico de Pizza via Canvas (alta resolução) ──────────────────────────
const gerarPizzaCanvas = (dados, cores) => new Promise((resolve) => {
  const SCALE = 3;
  const size   = 300 * SCALE;
  const canvas = document.createElement('canvas');
  canvas.width  = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const cx  = size / 2;
  const cy  = size / 2;
  const r   = size * 0.38;
  const total = dados.reduce((s, d) => s + d.value, 0);

  let startAngle = -Math.PI / 2;
  dados.forEach((d, i) => {
    const slice = total > 0 ? (d.value / total) * 2 * Math.PI : 0;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, r, startAngle, startAngle + slice);
    ctx.closePath();
    ctx.fillStyle = cores[i % cores.length];
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 4;
    ctx.stroke();
    if (slice > 0.25) {
      const mid = startAngle + slice / 2;
      const pct = ((d.value / total) * 100).toFixed(1);
      ctx.fillStyle    = '#fff';
      ctx.font         = `bold ${32}px Arial`;
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${pct}%`, cx + Math.cos(mid) * r * 0.65, cy + Math.sin(mid) * r * 0.65);
    }
    startAngle += slice;
  });
  resolve(canvas.toDataURL('image/png'));
});

const generatePdfRelatorioOrcamentosServico = async (data, filtro) => {
  if (!data) return;

  const pdfDoc  = await PDFDocument.create();
  const fontReg = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold= await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const W = 595.28; const H = 841.89;
  const ML = 40; const MR = 40;
  const CW = W - ML - MR;

  const C = {
    cinzaChumbo: rgbHex(coresHex.chumbo),
    cinzaMedio:  rgbHex(coresHex.cinzaMedio),
    cinzaClaro:  rgbHex(coresHex.cinzaClaro),
    laranja:     rgbHex(coresHex.laranja),
    laranjaClaro:rgbHex(coresHex.laranjaClaro),
    laranjaEsc:  rgbHex(coresHex.laranjaEscuro),
    branco:      rgb(1, 1, 1),
    preto:       rgb(0.133, 0.133, 0.133),
    borda:       rgbHex(coresHex.borda),
  };

      const CORES_HEX = [
    coresHex.laranja,
    coresHex.chumbo,
    escurecer(coresHex.laranja, 0.30),
    coresHex.cinzaMedio,
    escurecer(coresHex.laranja, 0.60),
    clarear(coresHex.chumbo, 0.45),
    escurecer(coresHex.laranja, 0.80),
    clarear(coresHex.cinzaMedio, 0.20),
  ];
  const CORES_RGB = CORES_HEX.map(rgbHex);

  const fmtBRL = (v) => parseFloat(v||0).toLocaleString('pt-BR', { style:'currency', currency:'BRL' });

  const drawRect  = (pg, x, y, w, h, color) => pg.drawRectangle({ x, y, width:w, height:h, color });
  const drawBRect = (pg, x, y, w, h, fill, border) => pg.drawRectangle({ x, y, width:w, height:h, color:fill, borderColor:border||C.borda, borderWidth:0.5 });
  const drawText  = (pg, t, x, y, s, f, color) => pg.drawText(String(t??''), { x, y, size:s, font:f, color });
  const drawLine  = (pg, x1, y1, x2, y2, color=C.borda, t=0.5) => pg.drawLine({ start:{x:x1,y:y1}, end:{x:x2,y:y2}, thickness:t, color });

  const tH = 14;
  const drawRow = (pg, cells, y) => {
    cells.forEach((c) => {
      drawBRect(pg, c.x, y-tH+2, c.w, tH, c.fill, c.bc||C.borda);
      if (c.text != null && c.text !== '') {
        const sz=c.sz||8; const fn=c.fn||fontReg;
        let tx = c.x+3;
        if (c.al==='r') tx = c.x+c.w-fn.widthOfTextAtSize(String(c.text),sz)-3;
        if (c.al==='c') tx = c.x+(c.w-fn.widthOfTextAtSize(String(c.text),sz))/2;
        drawText(pg, c.text, tx, y-tH+5, sz, fn, c.cl||C.preto);
      }
    });
    return y-tH;
  };

  // ── Logo ──────────────────────────────────────────────────────────────────
  let logoImage = null;
  let empresa = null;
  try {
    empresa = await getEmpresaAtual();
    if (empresa?.logoBranca) {
      const base64 = empresa.logoBranca.split(',')[1];
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
      logoImage = await pdfDoc.embedPng(bytes);
    }
  } catch (e) { /* sem logo */ }

  const usuarioLogado = JSON.parse(localStorage.getItem('userData') || '{}');
  const solicitante   = usuarioLogado?.nome || '—';

  const periodo = [
    filtro?.datainicial ? new Date(filtro.datainicial).toLocaleDateString('pt-BR') : '',
    filtro?.datafinal   ? new Date(filtro.datafinal).toLocaleDateString('pt-BR')   : '',
  ].filter(Boolean).join(' até ');

  // ── Cabeçalho ─────────────────────────────────────────────────────────────
  const TITULO = 'Relatório de Orçamentos de Serviço';
  const drawHeader = (pg, titulo) => {
    drawRect(pg, 0, H-44, W, 44, C.cinzaChumbo);
    drawRect(pg, 0, H-44, 6, 44, C.laranja);
    if (logoImage) {
      const d = logoImage.scaleToFit(70, 28);
      pg.drawImage(logoImage, { x:ML, y:H-39, width:d.width, height:d.height });
    } else {
      drawText(pg, empresa?.nome || 'Marcki Engenharia', ML, H - 26, 11, fontBold, C.branco);
    }
    drawText(pg, titulo, W-MR-fontBold.widthOfTextAtSize(titulo,10), H-24, 10, fontBold, C.branco);
    if (periodo) drawText(pg, `Período: ${periodo}`, W-MR-fontReg.widthOfTextAtSize(`Período: ${periodo}`,8), H-36, 8, fontReg, rgb(0.8,0.8,0.8));
    return H-58;
  };

  const drawFooter = (pg) => {
    drawRect(pg, 0, 0, W, 26, C.cinzaChumbo);
    drawRect(pg, 0, 0, 6, 26, C.laranja);
    drawText(pg, `Solicitante: ${solicitante}`, ML+8, 9, 7, fontReg, rgb(0.8,0.8,0.8));
    drawText(pg, `Gerado em ${new Date().toLocaleDateString('pt-BR')}`, W-MR-70, 9, 7, fontReg, rgb(0.8,0.8,0.8));
  };

  const r = data.resumo || {};
  const tabela = data.tabela || [];

  // ── Gera pizzas via canvas ─────────────────────────────────────────────────
  const dadosQtd = (data.graficoQuantidade||[]).filter(d=>d.valor>0).map(d=>({name:d.etapa, value:d.valor}));
  const dadosTipoServico = (data.porTipoServico||[]).filter(d=>d.quantidade>0).map(d=>({name:d.grupo, value:d.quantidade}));

  let pizzaQtdImg = null;
  let pizzaTipoImg = null;
  if (dadosQtd.length > 0) {
    const url  = await gerarPizzaCanvas(dadosQtd, CORES_HEX);
    const bytes = await fetch(url).then(r => r.arrayBuffer());
    pizzaQtdImg = await pdfDoc.embedPng(bytes);
  }
  if (dadosTipoServico.length > 0) {
    const url  = await gerarPizzaCanvas(dadosTipoServico, CORES_HEX);
    const bytes = await fetch(url).then(r => r.arrayBuffer());
    pizzaTipoImg = await pdfDoc.embedPng(bytes);
  }

  // ============================================================
  // PG 1 — KPIs + PIZZAS
  // ============================================================
  const p1 = pdfDoc.addPage([W, H]);
  let y1 = drawHeader(p1, TITULO);
  y1 -= 10;

  if (periodo) { drawText(p1, `De ${periodo}`, ML, y1, 8, fontReg, C.cinzaMedio); y1 -= 14; }
  if (filtro?.servico) { drawText(p1, `Serviço: ${filtro.servico}`, ML, y1, 8, fontReg, C.laranja); y1 -= 14; }

  // KPIs (sem kWp — não se aplica a serviço)
  const kpiW = (CW-10)/3;
  const kpiH = 36;
  const kpis = [
    { label: 'Ticket Médio (R$)', valor: fmtBRL(r.ticketMedioReais) },
    { label: 'Taxa de Conversão', valor: `${Number(r.taxaConversao||0).toFixed(1)}%` },
    { label: 'Taxa de Perdidos',  valor: `${Number(r.taxaPerdidos||0).toFixed(1)}%` },
  ];
  kpis.forEach((kpi, i) => {
    const x = ML + i*(kpiW+5);
    drawBRect(p1, x, y1-kpiH, kpiW, kpiH, C.laranjaClaro, C.laranja);
    drawRect(p1, x, y1-kpiH, 3, kpiH, C.laranja);
    drawText(p1, kpi.label, x+6, y1-13, 7, fontReg, C.cinzaMedio);
    drawText(p1, kpi.valor, x+6, y1-28, 10, fontBold, C.cinzaChumbo);
  });
  y1 -= kpiH+16;

  // Pizzas lado a lado — Quantidade por Etapa / Quantidade por Tipo de Serviço
  const pizzaSize = 130;
  const pizzaY    = y1-pizzaSize-10;
  if (pizzaQtdImg) {
    drawText(p1, 'Quantidade por Etapa', ML+10, y1-4, 8, fontBold, C.preto);
    p1.drawImage(pizzaQtdImg, { x:ML, y:pizzaY, width:pizzaSize, height:pizzaSize });
    let legY = y1-12;
    dadosQtd.forEach((d, i) => {
      const tot = dadosQtd.reduce((s,x)=>s+x.value,0);
      const pct = tot > 0 ? ((d.value/tot)*100).toFixed(1) : '0';
      drawRect(p1, ML+pizzaSize+8, legY-7, 8, 8, CORES_RGB[i%CORES_RGB.length]);
      const label = d.name.length>20 ? `${d.name.substring(0,20)}...` : d.name;
      drawText(p1, `${label}: ${pct}%`, ML+pizzaSize+20, legY-5, 6, fontReg, C.preto);
      legY -= 12;
    });
  }
  if (pizzaTipoImg) {
    const x2 = ML+CW/2+10;
    drawText(p1, 'Quantidade por Tipo de Serviço', x2, y1-4, 8, fontBold, C.preto);
    p1.drawImage(pizzaTipoImg, { x:x2, y:pizzaY, width:pizzaSize, height:pizzaSize });
    let legY = y1-12;
    dadosTipoServico.forEach((d, i) => {
      const tot = dadosTipoServico.reduce((s,x)=>s+x.value,0);
      const pct = tot > 0 ? ((d.value/tot)*100).toFixed(1) : '0';
      drawRect(p1, x2+pizzaSize+8, legY-7, 8, 8, CORES_RGB[i%CORES_RGB.length]);
      const label = d.name.length>18 ? `${d.name.substring(0,18)}...` : d.name;
      drawText(p1, `${label}: ${pct}%`, x2+pizzaSize+20, legY-5, 6, fontReg, C.preto);
      legY -= 12;
    });
  }
  y1 = pizzaY-20;

  // Gráfico de barras — Por Origem do Atendimento
  const dadosOrigem = (data.porOrigem||[]).map(g=>({
    name:g.grupo,
    value:g.tabela?.reduce((s,e)=>s+e.quantidade,0)||0,
  })).filter(d=>d.value>0);

  const barChartH = 185;

  const gerarBarrasVertCanvas = (dados, cores, dispW, dispH) => new Promise((resolve2) => {
    const SCALE=3;
    const cW2=Math.round(dispW*SCALE);
    const cH2=Math.round(dispH*SCALE);
    const cv=document.createElement('canvas');
    cv.width=cW2; cv.height=cH2;
    const ctx2=cv.getContext('2d');

    const padX  = 10*SCALE;
    const padTop= 18*SCALE;
    const padBot= 34*SCALE;
    const baseY = cH2-padBot;
    const topY  = padTop;
    const chartH= baseY-topY;

    const maxV=Math.max(...dados.map(d=>d.value),1);
    const n=dados.length;
    const slot=(cW2-padX*2)/n;
    const barW2=Math.min(slot*0.55,70*SCALE);

    ctx2.fillStyle='#F5F5F5'; ctx2.fillRect(0,0,cW2,cH2);
    for(let g=0;g<=4;g+=1){
      const gy=baseY-(g/4)*chartH;
      ctx2.strokeStyle='#DDD'; ctx2.lineWidth=1*SCALE;
      ctx2.beginPath(); ctx2.moveTo(padX,gy); ctx2.lineTo(cW2-padX,gy); ctx2.stroke();
    }

    const quebraRotulo = (texto, maxW) => {
      const palavras=String(texto).split(' ');
      const linhas=[]; let atual='';
      palavras.forEach(p=>{
        const teste=atual?`${atual} ${p}`:p;
        if(ctx2.measureText(teste).width<=maxW||!atual){ atual=teste; }
        else { linhas.push(atual); atual=p; }
      });
      if(atual) linhas.push(atual);
      const out=linhas.slice(0,2);
      if(linhas.length>2){
        let ult=out[1];
        while(ult.length>0&&ctx2.measureText(`${ult}...`).width>maxW){ ult=ult.slice(0,-1); }
        out[1]=`${ult}...`;
      }
      return out.map(ln=>{
        if(ctx2.measureText(ln).width<=maxW) return ln;
        let s=ln;
        while(s.length>0&&ctx2.measureText(`${s}...`).width>maxW){ s=s.slice(0,-1); }
        return `${s}...`;
      });
    };

    ctx2.textAlign='center';
    dados.forEach((d,i)=>{
      const barH=(d.value/maxV)*chartH;
      const x2=padX+i*slot+(slot-barW2)/2;
      const rad=Math.max(0,Math.min(4*SCALE,barW2/2,barH/2));
      ctx2.fillStyle=cores[i%cores.length];
      ctx2.beginPath(); ctx2.roundRect(x2,baseY-barH,barW2,barH,rad); ctx2.fill();
      ctx2.fillStyle='#333'; ctx2.font=`bold ${13*SCALE}px Arial`;
      ctx2.fillText(String(d.value),x2+barW2/2,baseY-barH-5*SCALE);

      ctx2.font=`${11*SCALE}px Arial`; ctx2.fillStyle='#555';
      const linhaH=13*SCALE;
      const linhas=quebraRotulo(d.name,slot-6*SCALE);
      linhas.forEach((ln,li)=>{
        ctx2.fillText(ln,x2+barW2/2,baseY+14*SCALE+li*linhaH);
      });
    });
    resolve2(cv.toDataURL('image/png'));
  });

  let barrasOrigemImg=null;
  if(dadosOrigem.length>0){
    const url=await gerarBarrasVertCanvas(dadosOrigem,CORES_HEX,CW,barChartH);
    const bytes=await fetch(url).then(resp=>resp.arrayBuffer());
    barrasOrigemImg=await pdfDoc.embedPng(bytes);
  }

  if (barrasOrigemImg) {
    drawText(p1, 'Orçamentos por Origem do Atendimento', ML, y1, 9, fontBold, C.preto);
    y1 -= 12;
    p1.drawImage(barrasOrigemImg, { x:ML, y:y1-barChartH, width:CW, height:barChartH });
    y1 -= barChartH+18;
  }

  drawFooter(p1);

  // ============================================================
  // PG 2 — TABELA DE ETAPAS
  // ============================================================
  const p2 = pdfDoc.addPage([W, H]);
  let y2 = drawHeader(p2, TITULO);
  y2 -= 16;

  drawRect(p2, ML, y2+2, CW, 16, C.cinzaChumbo);
  drawText(p2, 'ORÇAMENTOS DE SERVIÇO POR ETAPA', ML+6, y2+7, 8, fontBold, C.branco);
  y2 -= 16;

  const c1=CW*0.55; const c2=CW*0.20; const c3=CW-c1-c2;
  y2 = drawRow(p2, [
    {x:ML,       w:c1, fill:C.laranja, text:'Etapa',      fn:fontBold, cl:C.branco},
    {x:ML+c1,    w:c2, fill:C.laranja, text:'Quantidade', fn:fontBold, cl:C.branco, al:'c'},
    {x:ML+c1+c2, w:c3, fill:C.laranja, text:'Valor (R$)', fn:fontBold, cl:C.branco, al:'r'},
  ], y2);

  tabela.forEach((e, i) => {
    if (y2 < 50) return;
    const fill = i%2===0 ? C.cinzaClaro : C.branco;
    y2 = drawRow(p2, [
      {x:ML,       w:c1, fill, text:e.etapa},
      {x:ML+c1,    w:c2, fill, text:String(e.quantidade), al:'c'},
      {x:ML+c1+c2, w:c3, fill, text:fmtBRL(e.valor), al:'r'},
    ], y2);
  });

  // Segmentação por tipo de serviço
  if ((data.porTipoServico||[]).length > 0) {
    y2 -= 12;
    drawRect(p2, ML, y2+2, CW, 16, C.cinzaChumbo);
    drawText(p2, 'SEGMENTAÇÃO POR TIPO DE SERVIÇO', ML+6, y2+7, 8, fontBold, C.branco);
    y2 -= 16;

    y2 = drawRow(p2, [
      {x:ML,       w:c1, fill:C.laranjaClaro, text:'Tipo de Serviço', fn:fontBold, cl:C.cinzaChumbo},
      {x:ML+c1,    w:c2, fill:C.laranjaClaro, text:'Quantidade',      fn:fontBold, cl:C.cinzaChumbo, al:'c'},
      {x:ML+c1+c2, w:c3, fill:C.laranjaClaro, text:'Valor (R$)',      fn:fontBold, cl:C.cinzaChumbo, al:'r'},
    ], y2);

    data.porTipoServico.forEach((g, i) => {
      if (y2 < 50) return;
      const fill = i%2===0 ? C.cinzaClaro : C.branco;
      y2 = drawRow(p2, [
        {x:ML,       w:c1, fill, text:(g.grupo||'—').substring(0,40)},
        {x:ML+c1,    w:c2, fill, text:String(g.quantidade), al:'c'},
        {x:ML+c1+c2, w:c3, fill, text:fmtBRL(g.valor), al:'r'},
      ], y2);
    });
  }

  drawLine(p2, ML, y2-8, W-MR, y2-8, C.laranja, 0.5);
  drawText(p2, `Taxa de Conversão ${Number(r.taxaConversao||0).toFixed(1)}% — Tx Perdidos ${Number(r.taxaPerdidos||0).toFixed(1)}%`, ML, y2-18, 7, fontReg, C.cinzaMedio);

  drawFooter(p2);

  // ============================================================
  // PG 3 — SEGMENTAÇÃO POR FORMA DE ATENDIMENTO
  // ============================================================
  const p3 = pdfDoc.addPage([W, H]);
  let y3 = drawHeader(p3, TITULO);
  y3 -= 16;

  let pgAtual = p3;
  let yAtual  = y3;

  const drawSegTable = (titulo, grupos) => {
    if (!grupos || grupos.length === 0) return;
    if (yAtual < 100) {
      drawFooter(pgAtual);
      pgAtual = pdfDoc.addPage([W, H]);
      yAtual  = drawHeader(pgAtual, TITULO);
      yAtual -= 16;
    }
    drawRect(pgAtual, ML, yAtual+2, CW, 16, C.cinzaChumbo);
    drawText(pgAtual, titulo, ML+6, yAtual+7, 8, fontBold, C.branco);
    yAtual -= 16;
    const sc1=CW*0.40; const sc2=CW*0.20; const sc3=CW*0.20; const sc4=CW-sc1-sc2-sc3;
    yAtual = drawRow(pgAtual, [
      {x:ML,            w:sc1, fill:C.laranja, text:'Grupo',        fn:fontBold, cl:C.branco},
      {x:ML+sc1,        w:sc2, fill:C.laranja, text:'Quantidade',   fn:fontBold, cl:C.branco, al:'c'},
      {x:ML+sc1+sc2,    w:sc3, fill:C.laranja, text:'Tx Conversão', fn:fontBold, cl:C.branco, al:'c'},
      {x:ML+sc1+sc2+sc3,w:sc4, fill:C.laranja, text:'Tx Perdidos',  fn:fontBold, cl:C.branco, al:'c'},
    ], yAtual);
    grupos.forEach((g, i) => {
      if (yAtual < 50) {
        drawFooter(pgAtual);
        pgAtual = pdfDoc.addPage([W, H]);
        yAtual  = drawHeader(pgAtual, TITULO);
        yAtual -= 16;
        yAtual = drawRow(pgAtual, [
          {x:ML,            w:sc1, fill:C.laranja, text:'Grupo',        fn:fontBold, cl:C.branco},
          {x:ML+sc1,        w:sc2, fill:C.laranja, text:'Quantidade',   fn:fontBold, cl:C.branco, al:'c'},
          {x:ML+sc1+sc2,    w:sc3, fill:C.laranja, text:'Tx Conversão', fn:fontBold, cl:C.branco, al:'c'},
          {x:ML+sc1+sc2+sc3,w:sc4, fill:C.laranja, text:'Tx Perdidos',  fn:fontBold, cl:C.branco, al:'c'},
        ], yAtual);
      }
      const fill = i%2===0 ? C.cinzaClaro : C.branco;
      const qtdTotal = g.tabela?.reduce((s,e)=>s+e.quantidade,0)||0;
      yAtual = drawRow(pgAtual, [
        {x:ML,            w:sc1, fill, text:(g.grupo||'—').substring(0,28)},
        {x:ML+sc1,        w:sc2, fill, text:String(qtdTotal), al:'c'},
        {x:ML+sc1+sc2,    w:sc3, fill, text:`${Number(g.resumo?.taxaConversao||0).toFixed(1)}%`, al:'c'},
        {x:ML+sc1+sc2+sc3,w:sc4, fill, text:`${Number(g.resumo?.taxaPerdidos||0).toFixed(1)}%`, al:'c'},
      ], yAtual);
    });
    yAtual -= 14;
  };

  drawSegTable('SEGMENTAÇÃO POR FORMA DE ATENDIMENTO', data.porOrigem);

  drawFooter(pgAtual);

  const pdfBytes = await pdfDoc.save();
  const blob     = new Blob([pdfBytes], { type:'application/pdf' });
  const url      = URL.createObjectURL(blob);
  const link     = document.createElement('a');
  link.href      = url;
  link.download  = 'relatorio-orcamentos-servico.pdf';
  link.click();
  URL.revokeObjectURL(url);
};

export default generatePdfRelatorioOrcamentosServico;